import type { ComponentType, PropsWithoutRef, FC } from 'react'
import { lazy } from 'react'
import type { ClientLoaderFunctionArgs } from 'react-router'
import { ClientOnly } from 'remix-utils/client-only'

export interface DynamicOptions {
  ssr?: boolean
}

export type LoadModuleResult<Props = {}> =
  | { default: ComponentType<Props> }
  | { Component: ComponentType<Props> }
  | ComponentType<Props>

export function normalizeModule<Props>(mod: LoadModuleResult<Props>): {
  default: ComponentType<Props>
} {
  if (typeof mod === 'function') return { default: mod }
  if ('default' in mod) return { default: mod.default }
  if ('Component' in mod) return { default: mod.Component }
  throw new Error('Invalid module result')
}

export function dynamic<Props = {}>(
  loadModule: () => Promise<LoadModuleResult<Props>>,
  opts?: DynamicOptions,
): FC<PropsWithoutRef<Props>> {
  const LazyComponent = lazy(async () => normalizeModule(await loadModule()))

  if (opts?.ssr === false) {
    return function ClientOnlyDynamicWrapper(props) {
      return (
        <ClientOnly>{() => <LazyComponent {...(props as any)} />}</ClientOnly>
      )
    }
  } else {
    return function DynamicWrapper(props) {
      return <LazyComponent {...(props as any)} />
    }
  }
}

export interface CreateHydratingClientLoaderOptions {
  /**
   * If set, the resolved loader payload is held in memory for this many
   * milliseconds. Subsequent `clientLoader` invocations within the TTL
   * skip `args.serverLoader()` entirely and synchronously return the
   * cached value, so SPA navigations (notably the browser back / forward
   * buttons) don't have to wait for the server loader to round-trip
   * before swapping route DOM. Use only on routes whose loader output
   * tolerates being mildly stale (the public home feed, anonymous
   * landing surfaces, etc.).
   *
   * Without this option the loader still runs through
   * `args.serverLoader()` on every navigation, matching the original
   * behaviour.
   */
  cacheTtlMs?: number
}

/**
 * Create a hydrating clientLoader from a client module that may export
 * a redirect-only loader.
 *
 * The client module's loader runs first and is responsible for handling
 * redirects by throwing (e.g. `throw redirect(...)`).
 * If it completes without throwing, server loader data is used so that
 * SSR meta tags remain consistent during hydration.
 */
export function createHydratingClientLoader<TServerData = unknown>(
  loadModule: () => Promise<{
    loader?: (args: ClientLoaderFunctionArgs) => unknown
  }>,
  options?: CreateHydratingClientLoaderOptions,
) {
  let cache: { data: TServerData; ts: number } | null = null

  const clientLoader = async (args: ClientLoaderFunctionArgs) => {
    const mod = await loadModule()
    // Client module loader only handles redirects via throw.
    // If it doesn't throw, fall through to server data for meta consistency.
    await mod.loader?.(args)

    // Fast-path: serve from in-memory cache when the route opts in.
    // This is what makes back-button SPA navigations to e.g. the home
    // route feel instant instead of stalling on
    // `?_routes=routes/_app._index` round-trip — without it the
    // previous route DOM stays mounted (often scroll-restored to a
    // section that visually looks like leaked content) until the
    // network resolves.
    if (
      options?.cacheTtlMs != null &&
      cache != null &&
      Date.now() - cache.ts < options.cacheTtlMs
    ) {
      return cache.data
    }

    // serverLoader is only available in SSR framework mode, not in CSR data-router
    const data = (await args.serverLoader?.()) as TServerData
    if (options?.cacheTtlMs != null) {
      cache = { data, ts: Date.now() }
    }
    return data
  }
  clientLoader.hydrate = true as const
  return clientLoader
}
